import { PINNED } from '@private/data-grid';

import { getApiUrl } from 'api/base';
import { IPaletteColor } from 'api/merchantTerminalThemes/types';
import {
  PRESET_IGNORE_FIELDS,
  PRESET_IGNORE_FIELDS_LIST,
} from 'components/Grids/DataGrid/Filters/utils/constants';
import {
  FILTER_OPERATIONS,
  IFilter,
} from 'components/Grids/DataGrid/Filters/utils/types';
import { PER_PAGE_OPTIONS } from 'constants/common';
import { THEME_VARIANT } from 'constants/defaultPalette';
import { settingsPalette } from 'routes/pages/AuthorizedStack/Settings/PortalSettings/utils/palette';
import { LANGUAGES } from 'store/userConfiguration';
import { TObject } from 'utils/types';

import {
  IAmountDisplayConfig,
  IGridColumnsSettings,
  IOrganizationSettings,
  IOrganizationTheme,
  IPagesConfigs,
  IPortalUserConfig,
  ISavedPreset,
} from './types';

const normalizePageSettings = (data: string): IGridColumnsSettings | undefined => {
  if (!data) {
    return;
  }

  let parsedData = {};

  try {
    parsedData = JSON.parse(data);
  } catch {
    // eslint-disable-next-line no-console
    console.warn(`Can't parse JSON`);
  }

  const normalizedColumns = Object.entries<TObject>(parsedData).map(([key, data]) => [
    key,
    {
      order: (data?.order as number) || 0,
      pinned: data?.pinned as PINNED,
      stretched: !!data?.stretched,
      visible: !!data?.visible,
    },
  ]);

  return Object.fromEntries(normalizedColumns);
};

const LIKE_OPERATIONS = [FILTER_OPERATIONS.LIKE, FILTER_OPERATIONS.NOT_LIKE];

const normalizePresetCondition = (data?: TObject): TObject => {
  const normalized: TObject = {
    operation: (data?.operation as FILTER_OPERATIONS) || '',
    value: (data?.value as IFilter['value']) || '',
  };

  if (data?.enabled === false) {
    normalized.enabled = false;
  }

  if (
    Array.isArray(data?.disabledIndexes) &&
    (data.disabledIndexes as unknown[]).every((i) => typeof i === 'number')
  ) {
    const disabledIndexes = data.disabledIndexes as number[];
    if (disabledIndexes.length) {
      normalized.disabledIndexes = disabledIndexes;
    }
  }

  return normalized;
};

export const normalizeFiltersPresets = (data: TObject): ISavedPreset => {
  let parsedData;

  try {
    if (data.filters) {
      parsedData = JSON.parse(data.filters as string);
    }
  } catch {
    // eslint-disable-next-line no-console
    console.warn(`Can't parse JSON`);
  }

  const normalizedFilters = Object.entries<TObject>(parsedData || {}).reduce<
    [string, TObject][]
  >((prev, [key, data]) => {
    if (!PRESET_IGNORE_FIELDS_LIST.includes(key as PRESET_IGNORE_FIELDS)) {
      const normalized = normalizePresetCondition(data);
      const additionalCondition = data?.additionalCondition as TObject | undefined;

      if (
        additionalCondition &&
        LIKE_OPERATIONS.includes(additionalCondition.operation as FILTER_OPERATIONS)
      ) {
        normalized.additionalCondition = normalizePresetCondition(additionalCondition);
      }

      prev.push([key, normalized]);
    }

    return prev;
  }, []);

  return {
    columns: normalizePageSettings(data?.columns as string),
    filters: Object.fromEntries(normalizedFilters),
    id: (data?._id as string) || '',
    isRecent: !!data?.isRecent,
    isSystem: !!data?.isSystem,
    name: (data?.name as string) || '',
  };
};

interface IPageData {
  activePreset: string;
  amountDisplay?: IAmountDisplayConfig;
  columns: string;
  expandTable?: boolean;
  presets: TObject[];
}

const normalizePages = (data: Record<string, IPageData>): IPagesConfigs => {
  const entries = Object.entries(data);

  const normalizedPageSettings: IPagesConfigs = {};

  entries?.forEach(([pageKey, pageSettings]) => {
    const { activePreset, amountDisplay, expandTable, presets } = pageSettings;
    normalizedPageSettings[pageKey] = {
      activePreset: (activePreset as string) || null,
      ...(amountDisplay ? { amountDisplay } : {}),
      expandTable: !!expandTable,
      presets: presets?.map(normalizeFiltersPresets) || [],
    };
  });

  return normalizedPageSettings;
};

type TPortalUserData = TObject & {
  settings: TSetting;
  systemSettings: TSetting;
};

type TSetting = TObject & {
  pages: Record<string, IPageData>;
};

const normalizeSettings = (data: TSetting) => {
  const customCalendarPeriodDays = data?.customCalendarPeriodDays as number | undefined;
  return {
    ...(customCalendarPeriodDays != null ? { customCalendarPeriodDays } : {}),
    groupEditConfirmation: !!data?.groupEditConfirmation,
    hideMaxClientsStats: !!data?.groupEditConfirmation,
    language: (data?.language as LANGUAGES) || LANGUAGES.EN,
    pages: normalizePages(data?.pages || {}) || {},
    recordsPerPage: (data?.recordsPerPage as number) || PER_PAGE_OPTIONS[0].value,
    rulesetDefaultCopyInclude: !!data?.rulesetDefaultCopyInclude,
    theme: (data?.theme as THEME_VARIANT) || THEME_VARIANT.DARK,
  };
};

export const normalizePortalUserConfig = (data: TPortalUserData): IPortalUserConfig => {
  return {
    settings: normalizeSettings(data?.settings),
    systemSettings: normalizeSettings(data?.systemSettings),
  };
};

const normalizePalette = (data: TObject): IPaletteColor => {
  return {
    hue: (data?.hue as number) || 0,
    name: (data?.name as string) || '',
    saturation: (data?.saturation as number) || 0,
  };
};

const getPalette = (palette: string | TObject[]) => {
  if (typeof palette === 'string') {
    return palette;
  }
  if (!palette) {
    return settingsPalette[0].id;
  }
  return palette.map(normalizePalette);
};

export const normalizeOrganizationTheme = (data: TObject): IOrganizationTheme => {
  let parsedPalette;

  try {
    parsedPalette = JSON.parse(data?.palette as string);
  } catch {
    // eslint-disable-next-line no-console
    console.warn(`Can't parse JSON`);
  }

  const organizationId = Number(data?.organizationId);

  return {
    createdAt: (data?.createdAt as string) || '',
    id: (data?.id as string) || '',
    name: (data?.name as string) || '',
    organizationId: Number.isFinite(organizationId) ? organizationId : 0,
    palette: getPalette(parsedPalette),
    preview: (data?.preview as string) || '',
    updatedAt: (data?.updatedAt as string) || '',
  };
};

export type IOrganizationThemeData = TObject & {
  themes?: TObject[];
};

export const normalizeOrganizationBase = (data: TObject) => {
  const organizationId = Number(data?.organizationId);

  return {
    createdAt: (data?.createdAt as string) || '',
    id: (data?.id as string) || '',
    logo: data?.logo ? getApiUrl('logo')(data.logo as string) : '',
    name: (data?.name as string) || '',
    organizationId: Number.isFinite(organizationId) ? organizationId : 0,
    updatedAt: (data?.updatedAt as string) || '',
  };
};

export type IOrganizationSettingsData = TObject & {
  activeTheme: TObject;
};

export const normalizePortalOrganizationSettings = (
  data: IOrganizationSettingsData,
): IOrganizationSettings => {
  return {
    ...normalizeOrganizationBase(data),
    activeTheme: normalizeOrganizationTheme(data.activeTheme),
  };
};
